import React, {useState, useEffect} from 'react';
import {Copyright, Header} from "../components";
import Typography from "@mui/material/Typography";
import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Button,
    Box,
    TextField,
    Grid,
    FormControlLabel,
    RadioGroup,
    FormLabel,
    Radio
} from "@material-ui/core";
import useStyles from "../assets/styles/useStyles";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router";
import useGET from "../hooks/useGET";
import usePOST from "../hooks/usePOST";

const EMAIL_RE = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

function AddUser() {
    const [formValues, setFormValues] = useState({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        password: '',
        confirmPassword: '',
        company: '',
        roles: ['ROLE_USER'],
    })
    const [utilValues, setUtilValues] = useState({
        selectedRole: "ROLE_USER",
        shownPhone: "",
    })
    const ROLE_HIERARCHY = {
        ROLE_MASTER: ["ROLE_MASTER", "ROLE_MANAGER", "ROLE_USER"],
        ROLE_MANAGER: ["ROLE_MANAGER", "ROLE_USER"],
        ROLE_USER: ["ROLE_USER"],
    };

    const [responseGet, sendGetRequest] = useGET();
    const [companies, setCompanies] = useState([])
    const [responsePost, sendPostRequest] = usePOST();
    const [userAdded, setUserAdded] = useState(false);
    const [error, setError] = useState({password: false, email: false, messages: []});

    const navigate = useNavigate();
    const reduxUser = useSelector(state => state.userReducer.user);

    const classes = useStyles();

    // check perm
    useEffect(() => {
        if (!reduxUser.roles?.includes('ROLE_MASTER')) {
            navigate("/")
        }
    }, [reduxUser.roles, navigate])

    // récupérer toutes les entreprises
    useEffect(() => {
        sendGetRequest({
            id: 'getCompanies',
            url: '/api/companies/getAllCompanies',
            headers: {
                Authorization: `Bearer ${reduxUser.token}`
            }
        })
    }, [sendGetRequest, reduxUser]);

    useEffect(() => {
        const response = responseGet

        if (!response) return;

        try {
            if (response.status < 200 || response.status >= 300) {
                console.log("error while retrieving companies")
            } else {
                setCompanies(response.data);
            }
        } catch (err) {
            console.log("error while retrieving companies: " + err)
        }

    }, [responseGet]);

    const submitHandler = async (event) => {
        event.preventDefault();

        const errorValues = {password: false, email: false, messages: []}

        if (!EMAIL_RE.test(formValues.email)) {
            errorValues.email = true;
            errorValues.messages.push("L'adresse mail n'est pas valide.");
        }
        if (formValues.confirmPassword !== formValues.password) {
            errorValues.password = true;
            errorValues.messages.push("Les mots de passe de correspondent pas.")
        }
        setError(errorValues)

        if (errorValues.messages.length > 0) {
            return;
        }

        await sendPostRequest({
            id: 'addUser',
            url: '/api/users/addNewUser',
            data: {
                firstname: formValues.firstName,
                lastname: formValues.lastName,
                email: formValues.email,
                phone: formValues.phone,
                password: formValues.password,
                roles: formValues.roles,
                company: formValues.company,
                status: true
            },
            headers: {
                Authorization: `Bearer ${reduxUser.token}`,
            },
        })
    }

    useEffect(() => {
        const response = responsePost['addUser'];
        if (!response) return;

        const errorValues = (message) => {
            return {
                email: false,
                password: false,
                messages: [message]
            }
        }

        try {
            if (response.status >= 200 && response.status < 300) {
                setUserAdded(true);
            } else if (response.status === 409) {
                setError(errorValues("Cette adresse email est déjà utilisée."));
            } else {
                setError(errorValues("Une erreur serveur est survenue."));
            }
        } catch (err) {
            setError(errorValues("Une erreur serveur est survenue."));
        }

    }, [responsePost])

    const handleChange = (e) => {
        const {name, value} = e.target;
        let newValue = value;

        if (name === 'phone') {
            newValue = value.replace(/\D/g, '').slice(0, 14);
        }
        if (name === 'roles') {
            setUtilValues({...utilValues, selectedRole: value});
            newValue = ROLE_HIERARCHY[value];
        }
        setFormValues((prev) => ({...prev, [name]: newValue}));
    }

    return <Grid container direction='column'>
        <Grid item>
            <Header/>
        </Grid>
        <Grid item style={{marginTop: 20}}>
            <div className={classes.paper}>
                <Typography
                    component="h1"
                    variant="h3"
                    align="center"
                    gutterBottom
                >
                    Ajouter un utilisateur
                </Typography>
                {error.messages.map((m, i) => (
                    <Typography id="error-info" key={i}
                                style={{display: "block"}}
                                className={classes.message} align="center">
                        {m}
                    </Typography>)
                )}

                {userAdded && (<Typography id="added-user"
                                           className={classes.confirm}
                                           align="center">
                        L'utilisateur a bien été ajouté.
                    </Typography>

                )}

                <form className={classes.form} onSubmit={submitHandler} required>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField variant="outlined" required
                                       fullWidth label="Prénom"
                                       name="firstName"
                                       value={formValues.firstName}
                                       onChange={handleChange}/>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField variant="outlined" required
                                       fullWidth label="Nom"
                                       name="lastName"
                                       value={formValues.lastName}
                                       onChange={handleChange}/>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField variant="outlined" required
                                       fullWidth label="Numéro de téléphone"
                                       name="phone"
                                       value={formValues.phone}
                                       onChange={handleChange}/>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField variant="outlined" required
                                       fullWidth label="Adresse email"
                                       name="email"
                                       value={formValues.email}
                                       error={error.email}
                                       onChange={handleChange}/>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField variant="outlined" required
                                       fullWidth label="Mot de passe" type="password"
                                       name="password"
                                       value={formValues.password}
                                       error={error.password}
                                       onChange={handleChange}/>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField variant="outlined" required
                                       fullWidth label="Confirmer le mot de passe" type="password"
                                       name="confirmPassword"
                                       value={formValues.confirmPassword}
                                       error={error.password}
                                       onChange={handleChange}/>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth variant="outlined" required>
                                <InputLabel id="company-label">Entreprise</InputLabel>
                                <Select
                                    labelId="company-label"
                                    id="company"
                                    name="company"
                                    value={formValues.company}
                                    onChange={handleChange}
                                    label="Entreprise"
                                >
                                    {companies.map((c) => (
                                        <MenuItem key={c.id} value={c.id}>
                                            {c.name} - {c.siret}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormLabel id="radio-roles">Rôles</FormLabel>
                            <RadioGroup
                                aria-labelledby="radio-roles"
                                defaultValue="ROLE_USER"
                                name="roles"
                                value={utilValues.selectedRole}
                                onChange={handleChange}
                                row
                            >
                                <FormControlLabel value="ROLE_MASTER"
                                                  control={<Radio/>}
                                                  label="Administrateur (ROLE_MASTER)"/>
                                <FormControlLabel value="ROLE_MANAGER"
                                                  control={<Radio/>}
                                                  label="Manager (ROLE_MANAGER)"/>
                                <FormControlLabel value="ROLE_USER"
                                                  control={<Radio/>}
                                                  label="Utilisateur (ROLE_USER)"/>
                            </RadioGroup>
                        </Grid>
                    </Grid>
                    <div style={{display: "flex", alignItems: "center", justifyContent: "center",}}>
                        <Button type="submit" variant="contained" className={classes.submit}>
                            ENVOYER
                        </Button>
                    </div>
                </form>
            </div>
        </Grid>
        <Box mt={5}>
            <Copyright/>
        </Box>
    </Grid>
}

export default AddUser;
